/**
 * GeoVera public homepage — enterprise-grade marketing surface.
 * Adoption Spine: free full product for humans; monetize commercial API / org scale.
 */
import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Container, Divider, Stack, Typography, Paper } from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import NearMeOutlinedIcon from '@mui/icons-material/NearMeOutlined';
import RadarOutlinedIcon from '@mui/icons-material/RadarOutlined';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { motion } from 'motion/react';
import { useAppSelector } from '../store/hooks';
import { PublicSiteChrome, publicPageSx } from './site/PublicSiteChrome';
import Footer from './Footer';

const TOKENS = {
  primary: '#1E40AF',
  secondary: '#3B82F6',
  accent: '#D97706',
  accentGlow: 'rgba(217, 119, 6, 0.35)',
  bgDark: '#0B132B',
  bgSurface: '#0F172A',
  bgCard: 'rgba(15, 23, 42, 0.75)',
  border: 'rgba(219, 234, 254, 0.15)',
  borderActive: 'rgba(59, 130, 246, 0.5)',
  textPrimary: '#F8FAFC',
  textSecondary: '#94A3B8',
  textHighlight: '#60A5FA',
  greenSignal: '#10B981',
};

const TRUST_BAR = [
  { label: 'India-first', sub: 'DIGIPIN national grid' },
  { label: 'PostGIS', sub: 'Production spatial DB' },
  { label: 'Cloud Run', sub: 'Asia South (Mumbai)' },
  { label: 'SIFA Geolabs', sub: 'Billing & support' },
  { label: 'Free forever', sub: 'Full map for humans' },
];

const SOLUTIONS = [
  {
    icon: <NearMeOutlinedIcon sx={{ fontSize: 28, color: TOKENS.accent }} />,
    audience: 'Surveyors & field teams',
    title: 'Field-ready WebGIS',
    body: 'Draw boundaries, measure, import/export standard GIS formats, and share public map links without installing desktop software.',
  },
  {
    icon: <BusinessOutlinedIcon sx={{ fontSize: 28, color: TOKENS.secondary }} />,
    audience: 'Municipalities & PSUs',
    title: 'Organisation scale',
    body: 'Enterprise design partners get onboarding, bulk DIGIPIN conversations, SLA dialogue, and a human support channel under SIFA Geolabs.',
  },
  {
    icon: <CodeOutlinedIcon sx={{ fontSize: 28, color: TOKENS.greenSignal }} />,
    audience: 'Developers & integrators',
    title: 'DIGIPIN API access',
    body: 'Request commercial API keys, integrate national location encoding into apps, and operate within fair-use or Studio quotas.',
  },
  {
    icon: <PublicOutlinedIcon sx={{ fontSize: 28, color: '#A855F7' }} />,
    audience: 'Citizens & educators',
    title: 'Open free map',
    body: 'Full browser WebGIS free forever — no credit card. Soft fair-use caps protect hosting; map tools stay unlocked.',
  },
];

const PLATFORM = [
  {
    icon: <NearMeOutlinedIcon sx={{ color: TOKENS.accent, fontSize: 28 }} />,
    title: 'DIGIPIN 4m × 4m grid',
    description:
      'National location encoding on the map and property cards — precise, shareable, India-standard addressing.',
    tag: 'India standard',
  },
  {
    icon: <DownloadOutlinedIcon sx={{ color: TOKENS.secondary, fontSize: 28 }} />,
    title: 'ClipToCAD & export',
    description:
      'The ClipToCAD QGIS plugin pastes selected parcels into AutoCAD as command-line text. The web map still exports standard GIS formats with CRS preserved.',
    tag: 'Interop',
  },
  {
    icon: <LayersOutlinedIcon sx={{ color: TOKENS.greenSignal, fontSize: 28 }} />,
    title: 'Multi-format vectors',
    description:
      'GeoJSON, Shapefile, KML/KMZ, and live PostGIS-backed layers for professional geospatial operations.',
    tag: 'GIS formats',
  },
  {
    icon: <RadarOutlinedIcon sx={{ color: '#EAB308', fontSize: 28 }} />,
    title: 'Measure & analyse',
    description:
      'Distance, area, buffers, and spatial tools for survey, planning, and infrastructure workflows.',
    tag: 'Analytics',
  },
  {
    icon: <CloudDoneOutlinedIcon sx={{ color: '#A855F7', fontSize: 28 }} />,
    title: 'Cloud projects',
    description:
      'Save workspaces to the cloud, reclaim guest sessions after sign-in, and pick up projects across devices.',
    tag: 'Workspace',
  },
  {
    icon: <CodeOutlinedIcon sx={{ color: '#EC4899', fontSize: 28 }} />,
    title: 'Developer platform',
    description:
      'DIGIPIN API keys, request workflow, and commercial licensing path for product and enterprise integration.',
    tag: 'API',
  },
];

const ENTERPRISE = [
  {
    icon: <SupportAgentOutlinedIcon sx={{ color: TOKENS.accent }} />,
    title: 'Dedicated onboarding',
    body: 'Design-partner rollout for survey firms, municipalities, and infrastructure teams.',
  },
  {
    icon: <HubOutlinedIcon sx={{ color: TOKENS.secondary }} />,
    title: 'Bulk DIGIPIN & API',
    body: 'Volume encoding, higher quotas, and commercial rights beyond Community fair-use.',
  },
  {
    icon: <VerifiedUserOutlinedIcon sx={{ color: TOKENS.greenSignal }} />,
    title: 'SLA conversation',
    body: 'Uptime, support channels, and operational expectations for mission-critical deployments.',
  },
  {
    icon: <SecurityOutlinedIcon sx={{ color: '#A855F7' }} />,
    title: 'Security posture',
    body: 'OAuth sessions, CSRF protection, rate limits, admin controls, and private/VPC roadmap.',
  },
];

/** Interactive map preview for the hero. */
const MapPreviewInteractive = () => {
  const [activeLayers, setActiveLayers] = useState<string[]>(['parcels', 'digipin', 'buffer']);
  const [mousePos, setMousePos] = useState({ x: 180, y: 140, lat: 18.5204, lng: 73.8567 });
  const [bufferSize, setBufferDistance] = useState(150);

  const toggleLayer = (id: string) => {
    setActiveLayers((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const lat = +(18.5204 + (y - 200) * -0.0002).toFixed(4);
    const lng = +(73.8567 + (x - 200) * 0.0002).toFixed(4);
    setMousePos({ x, y, lat, lng });
  };

  return (
    <Box
      onMouseMove={handleMouseMove}
      sx={{
        position: 'relative',
        minHeight: { xs: 420, sm: 520 },
        border: `1px solid ${TOKENS.borderActive}`,
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: TOKENS.bgDark,
        boxShadow: `0 24px 80px rgba(0, 0, 0, 0.55), 0 0 28px ${TOKENS.accentGlow}`,
        backgroundImage: `
          radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.14) 0%, transparent 70%),
          linear-gradient(135deg, rgba(11, 19, 43, 0.95), rgba(15, 23, 42, 0.98))
        `,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.22,
          backgroundImage: `
            linear-gradient(rgba(96, 165, 250, 0.2) 1px, transparent 1px),
            linear-gradient(90deg, rgba(96, 165, 250, 0.2) 1px, transparent 1px)
          `,
          backgroundSize: '40px 48px',
        }}
      />

      <svg
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        <defs>
          <pattern id="digipinGrid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path
              d="M 40 0 L 0 0 0 40"
              fill="none"
              stroke="rgba(217, 119, 6, 0.25)"
              strokeWidth="1"
              strokeDasharray="2,2"
            />
          </pattern>
        </defs>
        {activeLayers.includes('digipin') && (
          <rect width="100%" height="100%" fill="url(#digipinGrid)" />
        )}
        {activeLayers.includes('parcels') && (
          <g>
            <polygon
              points="80,120 280,90 380,240 220,340 100,280"
              fill="rgba(59, 130, 246, 0.12)"
              stroke="#60A5FA"
              strokeWidth="2"
            />
            <polygon
              points="280,90 480,140 440,310 380,240"
              fill="rgba(16, 185, 129, 0.12)"
              stroke="#10B981"
              strokeWidth="2"
            />
          </g>
        )}
        {activeLayers.includes('buffer') && (
          <circle
            cx={mousePos.x}
            cy={mousePos.y}
            r={bufferSize / 2}
            fill="rgba(217, 119, 6, 0.18)"
            stroke="#D97706"
            strokeWidth="2"
            strokeDasharray="4,4"
          />
        )}
        <circle cx={mousePos.x} cy={mousePos.y} r="6" fill="#D97706" />
        <circle
          cx={mousePos.x}
          cy={mousePos.y}
          r="16"
          fill="none"
          stroke="#F59E0B"
          strokeWidth="1"
          opacity="0.75"
        />
      </svg>

      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          right: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          zIndex: 10,
        }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {[
            { id: 'parcels', label: 'Parcels', color: '#60A5FA' },
            { id: 'digipin', label: 'DIGIPIN grid', color: '#F59E0B' },
            { id: 'buffer', label: 'Buffer', color: '#10B981' },
          ].map((layer) => {
            const active = activeLayers.includes(layer.id);
            return (
              <Chip
                key={layer.id}
                label={layer.label}
                size="small"
                onClick={() => toggleLayer(layer.id)}
                sx={{
                  bgcolor: active ? 'rgba(30, 58, 138, 0.85)' : 'rgba(15, 23, 42, 0.7)',
                  color: active ? '#F8FAFC' : '#94A3B8',
                  border: `1px solid ${active ? layer.color : TOKENS.border}`,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  backdropFilter: 'blur(8px)',
                }}
              />
            );
          })}
        </Stack>
        <Chip
          icon={
            <Box
              sx={{
                width: 6,
                height: 6,
                bgcolor: TOKENS.greenSignal,
                borderRadius: '50%',
                ml: 0.5,
              }}
            />
          }
          label="LIVE PREVIEW"
          size="small"
          sx={{
            bgcolor: 'rgba(16, 185, 129, 0.15)',
            color: TOKENS.greenSignal,
            border: '1px solid rgba(16, 185, 129, 0.3)',
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.08em',
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          right: 16,
          p: 1.5,
          bgcolor: 'rgba(15, 23, 42, 0.85)',
          border: `1px solid ${TOKENS.border}`,
          borderRadius: 2,
          backdropFilter: 'blur(12px)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 10,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Stack spacing={0.5}>
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontSize: 11,
              color: TOKENS.textHighlight,
              letterSpacing: '0.05em',
            }}
          >
            {mousePos.lat}° N · {mousePos.lng}° E
          </Typography>
          <Typography sx={{ fontSize: 10, color: TOKENS.textSecondary }}>
            DIGIPIN · EPSG:4326 · India sample
          </Typography>
        </Stack>
        {activeLayers.includes('buffer') && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: 10, color: TOKENS.textSecondary }}>
              Buffer: {bufferSize}m
            </Typography>
            <input
              type="range"
              min="50"
              max="300"
              value={bufferSize}
              onChange={(e) => setBufferDistance(+e.target.value)}
              style={{ width: '80px', accentColor: TOKENS.accent }}
            />
          </Stack>
        )}
      </Box>
    </Box>
  );
};

const sectionLabelSx = {
  fontFamily: 'monospace',
  color: TOKENS.accent,
  letterSpacing: '0.12em',
  fontSize: 11,
  fontWeight: 700,
} as const;

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isRestoring } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (!isRestoring && isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, isRestoring, navigate]);

  if (isAuthenticated) return null;

  return (
    <Box
      sx={{
        ...publicPageSx,
        bgcolor: TOKENS.bgDark,
        color: TOKENS.textPrimary,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <PublicSiteChrome title="GeoVera" subtitle="Enterprise WebGIS" active="home" />

      <Box component="main">
        {/* HERO */}
        <Container maxWidth="xl" sx={{ pt: { xs: 5, md: 9 }, pb: { xs: 6, md: 10 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1.1fr' },
              gap: { xs: 5, lg: 8 },
              alignItems: 'center',
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 2.5 }}
                flexWrap="wrap"
                useFlexGap
              >
                <Chip
                  label="India · DIGIPIN · WebGIS"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(217, 119, 6, 0.15)',
                    color: '#FBBF24',
                    fontWeight: 700,
                    fontSize: 11,
                    border: '1px solid rgba(217, 119, 6, 0.35)',
                  }}
                />
                <Chip
                  label="Free forever for humans"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(16, 185, 129, 0.12)',
                    color: TOKENS.greenSignal,
                    fontWeight: 700,
                    fontSize: 11,
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                  }}
                />
              </Stack>

              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: '2.35rem', sm: '3.25rem', md: '3.75rem' },
                  fontWeight: 800,
                  letterSpacing: '-0.04em',
                  lineHeight: 1.05,
                  mb: 2.5,
                  background: 'linear-gradient(180deg, #FFFFFF 0%, #CBD5E1 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Enterprise WebGIS for India.
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    color: TOKENS.accent,
                    WebkitTextFillColor: TOKENS.accent,
                  }}
                >
                  Free for every human.
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1.05rem', md: '1.15rem' },
                  color: TOKENS.textSecondary,
                  lineHeight: 1.65,
                  maxWidth: 560,
                  mb: 3.5,
                }}
              >
                GeoVera is a production spatial platform: browser map tools, DIGIPIN national grid,
                cloud projects, public shares, and commercial API paths — built for surveyors,
                municipalities, and developers. Monetise only when you need org scale or commercial
                rights.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 4 }}>
                <Button
                  component={RouterLink}
                  to="/map"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowOutwardIcon />}
                  sx={{
                    bgcolor: TOKENS.accent,
                    color: '#FFF',
                    fontWeight: 700,
                    px: 3.25,
                    py: 1.4,
                    borderRadius: 2,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    boxShadow: `0 8px 24px ${TOKENS.accentGlow}`,
                    '&:hover': { bgcolor: '#B45309' },
                  }}
                >
                  Open free map — no signup
                </Button>
                <Button
                  component={RouterLink}
                  to="/pricing"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: TOKENS.border,
                    color: TOKENS.textPrimary,
                    fontWeight: 600,
                    px: 2.75,
                    py: 1.4,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: TOKENS.secondary,
                      bgcolor: 'rgba(59, 130, 246, 0.1)',
                    },
                  }}
                >
                  Pricing & Enterprise
                </Button>
                <Button
                  href="mailto:support@geovera.in?subject=GeoVera%20Enterprise%20inquiry"
                  variant="text"
                  size="large"
                  sx={{
                    color: TOKENS.textHighlight,
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 1.5,
                  }}
                >
                  Contact sales
                </Button>
              </Stack>

              <Stack
                direction="row"
                spacing={3}
                divider={
                  <Divider orientation="vertical" flexItem sx={{ borderColor: TOKENS.border }} />
                }
                flexWrap="wrap"
                useFlexGap
              >
                {[
                  { v: '4m × 4m', l: 'DIGIPIN precision' },
                  { v: 'PostGIS', l: 'Spatial backend' },
                  { v: '₹0', l: 'Community forever' },
                ].map((s) => (
                  <Box key={s.l}>
                    <Typography
                      sx={{ fontWeight: 800, fontSize: '1.2rem', color: TOKENS.textHighlight }}
                    >
                      {s.v}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: TOKENS.textSecondary }}>
                      {s.l}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.65, delay: 0.12 }}
            >
              <MapPreviewInteractive />
            </motion.div>
          </Box>
        </Container>

        {/* TRUST BAR */}
        <Box
          sx={{
            borderTop: `1px solid ${TOKENS.border}`,
            borderBottom: `1px solid ${TOKENS.border}`,
            bgcolor: 'rgba(15, 23, 42, 0.85)',
            py: 2.5,
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(5, 1fr)',
                },
                gap: 2,
              }}
            >
              {TRUST_BAR.map((t) => (
                <Box key={t.label} sx={{ textAlign: { xs: 'left', md: 'center' }, px: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 14, letterSpacing: '-0.02em' }}>
                    {t.label}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: TOKENS.textSecondary, mt: 0.25 }}>
                    {t.sub}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>

        {/* SOLUTIONS */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }}>
          <Stack spacing={1.5} sx={{ maxWidth: 640, mb: 5 }}>
            <Typography sx={sectionLabelSx}>WHO IT SERVES</Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 800, letterSpacing: '-0.03em', fontSize: { xs: 28, md: 36 } }}
            >
              Built for professionals. Open to everyone.
            </Typography>
            <Typography sx={{ color: TOKENS.textSecondary, fontSize: '1.05rem', lineHeight: 1.6 }}>
              One platform from citizen map use to enterprise DIGIPIN programmes — without locking
              core tools behind a paywall.
            </Typography>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            {SOLUTIONS.map((s, idx) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: idx * 0.06 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    bgcolor: TOKENS.bgCard,
                    border: `1px solid ${TOKENS.border}`,
                    borderRadius: 3,
                    transition: 'border-color 0.2s ease, transform 0.2s ease',
                    '&:hover': {
                      borderColor: TOKENS.borderActive,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: 'rgba(30, 58, 138, 0.35)',
                        border: `1px solid ${TOKENS.border}`,
                        flexShrink: 0,
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Box>
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 700, color: TOKENS.secondary, mb: 0.5 }}
                      >
                        {s.audience}
                      </Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 0.75 }}>
                        {s.title}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 14, color: TOKENS.textSecondary, lineHeight: 1.6 }}
                      >
                        {s.body}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>

        {/* PLATFORM CAPABILITIES */}
        <Box
          sx={{
            bgcolor: TOKENS.bgSurface,
            borderTop: `1px solid ${TOKENS.border}`,
            borderBottom: `1px solid ${TOKENS.border}`,
          }}
        >
          <Container maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }}>
            <Stack spacing={1.5} textAlign="center" sx={{ maxWidth: 680, mx: 'auto', mb: 6 }}>
              <Typography sx={{ ...sectionLabelSx, textAlign: 'center' }}>PLATFORM</Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: 800, letterSpacing: '-0.03em', fontSize: { xs: 28, md: 36 } }}
              >
                Production geospatial operations
              </Typography>
              <Typography sx={{ color: TOKENS.textSecondary, fontSize: '1.05rem' }}>
                Full-stack WebGIS engineered for land, infrastructure, and developer workflows — not
                a toy demo.
              </Typography>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                gap: 2.5,
              }}
            >
              {PLATFORM.map((feature, idx) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: idx * 0.05 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      bgcolor: 'rgba(11, 19, 43, 0.55)',
                      border: `1px solid ${TOKENS.border}`,
                      borderRadius: 3,
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        borderColor: TOKENS.borderActive,
                        boxShadow: '0 12px 28px rgba(0,0,0,0.35)',
                      },
                    }}
                  >
                    <Stack spacing={1.75}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box sx={{ p: 1.1, bgcolor: 'rgba(30, 58, 138, 0.3)', borderRadius: 2 }}>
                          {feature.icon}
                        </Box>
                        <Chip
                          label={feature.tag}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.05)',
                            color: TOKENS.textSecondary,
                            fontSize: 10,
                            fontWeight: 600,
                          }}
                        />
                      </Stack>
                      <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
                        {feature.title}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 13.5, color: TOKENS.textSecondary, lineHeight: 1.6 }}
                      >
                        {feature.description}
                      </Typography>
                    </Stack>
                  </Paper>
                </motion.div>
              ))}
            </Box>
          </Container>
        </Box>

        {/* ENTERPRISE */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }} id="enterprise">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '0.9fr 1.1fr' },
              gap: { xs: 4, lg: 6 },
              alignItems: 'start',
            }}
          >
            <Stack spacing={2}>
              <Typography sx={sectionLabelSx}>ENTERPRISE</Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: 800, letterSpacing: '-0.03em', fontSize: { xs: 28, md: 34 } }}
              >
                Ready for organisations that cannot run on fair-use alone
              </Typography>
              <Typography
                sx={{ color: TOKENS.textSecondary, fontSize: '1.05rem', lineHeight: 1.65 }}
              >
                Community stays free forever. Enterprise is for municipalities, PSUs, large survey
                firms, and product teams that need volume, SLAs, and a named partner under{' '}
                <strong style={{ color: TOKENS.textPrimary }}>SIFA Geolabs</strong>.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
                <Button
                  component={RouterLink}
                  to="/pricing#studio-checkout"
                  variant="contained"
                  endIcon={<ArrowOutwardIcon />}
                  onClick={() => {
                    /* scroll handled by hash on pricing */
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    bgcolor: TOKENS.accent,
                    px: 2.5,
                    py: 1.2,
                    '&:hover': { bgcolor: '#B45309' },
                  }}
                >
                  Talk to Enterprise
                </Button>
                <Button
                  component={RouterLink}
                  to="/pricing"
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: TOKENS.border,
                    color: TOKENS.textPrimary,
                  }}
                >
                  Compare tiers
                </Button>
              </Stack>
              <Typography sx={{ fontSize: 12, color: '#64748B', pt: 1 }}>
                Roadmap: seats, roles, audit export, SSO, private/VPC deploy — design partners help
                prioritise.
              </Typography>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              {ENTERPRISE.map((item) => (
                <Paper
                  key={item.title}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    bgcolor: TOKENS.bgCard,
                    border: `1px solid ${TOKENS.border}`,
                    borderRadius: 2.5,
                  }}
                >
                  <Box sx={{ mb: 1.25 }}>{item.icon}</Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.5 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: TOKENS.textSecondary, lineHeight: 1.55 }}>
                    {item.body}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        </Container>

        {/* SECURITY / OPS STRIP */}
        <Box sx={{ bgcolor: 'rgba(15, 23, 42, 0.9)', borderTop: `1px solid ${TOKENS.border}` }}>
          <Container maxWidth="lg" sx={{ py: { xs: 5, md: 6 } }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
                gap: 3,
              }}
            >
              {[
                {
                  icon: <SecurityOutlinedIcon sx={{ color: TOKENS.secondary }} />,
                  title: 'Secure by default',
                  body: 'OAuth, session cookies, CSRF, rate limits, input sanitisation.',
                },
                {
                  icon: <StorageOutlinedIcon sx={{ color: TOKENS.accent }} />,
                  title: 'Spatial data plane',
                  body: 'PostgreSQL + PostGIS on Cloud SQL (asia-south1).',
                },
                {
                  icon: <SpeedOutlinedIcon sx={{ color: TOKENS.greenSignal }} />,
                  title: 'Operated in India region',
                  body: 'API on Cloud Run · frontend App Hosting · low latency for IN users.',
                },
                {
                  icon: <GroupsOutlinedIcon sx={{ color: '#A855F7' }} />,
                  title: 'Admin controls',
                  body: 'User disable, force-unpublish, leads, payments, DIGIPIN key admin.',
                },
              ].map((c) => (
                <Stack key={c.title} spacing={1}>
                  {c.icon}
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{c.title}</Typography>
                  <Typography sx={{ fontSize: 13, color: TOKENS.textSecondary, lineHeight: 1.5 }}>
                    {c.body}
                  </Typography>
                </Stack>
              ))}
            </Box>
          </Container>
        </Box>

        {/* FINAL CTA */}
        <Container maxWidth="md" sx={{ py: { xs: 8, md: 10 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6.5 },
              textAlign: 'center',
              borderRadius: 4,
              border: `1px solid ${TOKENS.borderActive}`,
              background:
                'linear-gradient(145deg, rgba(30, 58, 138, 0.45) 0%, rgba(15, 23, 42, 0.95) 55%, rgba(11, 19, 43, 0.98) 100%)',
              boxShadow: `0 20px 60px rgba(0, 0, 0, 0.45), 0 0 36px ${TOKENS.accentGlow}`,
            }}
          >
            <Stack spacing={2.5} alignItems="center">
              <Chip
                label="START IN MINUTES"
                size="small"
                sx={{
                  bgcolor: TOKENS.accent,
                  color: '#FFF',
                  fontWeight: 800,
                  fontSize: 10,
                  letterSpacing: '0.1em',
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  fontSize: { xs: 26, md: 34 },
                  maxWidth: 560,
                }}
              >
                Production WebGIS. Free map today. Enterprise when you scale.
              </Typography>
              <Typography sx={{ color: TOKENS.textSecondary, fontSize: '1.05rem', maxWidth: 480 }}>
                No credit card for Community. Studio pays via Razorpay (SIFA Geolabs). Enterprise is
                a conversation.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
                <Button
                  component={RouterLink}
                  to="/map"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowOutwardIcon />}
                  sx={{
                    bgcolor: TOKENS.accent,
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 3.5,
                    py: 1.4,
                    '&:hover': { bgcolor: '#B45309' },
                  }}
                >
                  Open free map
                </Button>
                <Button
                  component={RouterLink}
                  to="/developers/digipin"
                  variant="outlined"
                  size="large"
                  startIcon={<CodeOutlinedIcon />}
                  sx={{
                    borderColor: TOKENS.border,
                    color: TOKENS.textPrimary,
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 3,
                    py: 1.4,
                  }}
                >
                  DIGIPIN API
                </Button>
              </Stack>
              <Stack
                direction="row"
                spacing={2}
                flexWrap="wrap"
                justifyContent="center"
                useFlexGap
                sx={{ pt: 1 }}
              >
                {[
                  'No feature jail on Community',
                  'Commercial rights on Studio',
                  'SLA path on Enterprise',
                ].map((t) => (
                  <Typography
                    key={t}
                    sx={{
                      fontSize: 12,
                      color: TOKENS.textSecondary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <CheckCircleOutlinedIcon sx={{ fontSize: 14, color: TOKENS.greenSignal }} />
                    {t}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          </Paper>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default HomePage;
